import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { Add, Refresh, Delete } from '@material-ui/icons';
import { BackButton } from '../../sc-utilities';
import MaterialTable from 'material-table';
import { hasPermission, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import FilePatternDelete from './FilePatternDelete';
import * as actions from './store/filePatternActions';
import * as action from '../../np-core/Auth/store/authActions';
import Toolbar from '../../sc-utilities/Toolbar'
import { MuiTableRows } from '../../../themes';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];
const tableRef = React.createRef();

const FilePatternList = props => {
    const { classes, deleting, translations, permissions, onFetchFilePatterns, onFetchFilePatternsCount, fetching, saveColumnsFilter, activeCustomer, onBulkDeleteFilePatterns, tableColumns, redirectHandler } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, filePatternIDs: [] });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const selectionPermissions = hasAtLeastOnePermission(permissions, ['file_pattern_delete', 'system_file_pattern_delete']);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        selectionProps: rowData => {
            let disabled = !selectionPermissions;
            rowData.tableData.disabled = disabled
            return disabled ? {
                disabled: true,
                checked: false
            } : {}
        },
        search: false,
        minBodyHeight: window.innerHeight - 290 < 300 ? 300 : window.innerHeight - 290,
        maxBodyHeight: window.innerHeight - 290,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        let count = await onFetchFilePatternsCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchFilePatterns(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return <div className={classes.tableTitle}>
            {BackButton(translations, classes.customTooltip)}
            <Typography className={classes.typography}>{translations['file_patterns'] || 'File patterns'}</Typography>
            {hasPermission(permissions, 'file_pattern_create') ? <Fab
                id="create_file_pattern"
                variant="extended"
                style={{ borderRadius: '1px' }}
                size="small"
                color="primary"
                aria-label="Add"
                className={classes.fab}
                onClick={() => redirectHandler(`/settings/file-pattern/create`)}
            >
                <Add size="small" />
                {translations["create_new"] || "Create new"}
            </Fab> : null}
            <Tooltip title={translations["refresh"] || "Refresh"}>
                <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                    <Refresh />
                </IconButton>
            </Tooltip>
        </div>
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, filePatternIDs: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteFilePatterns({ FilePatternIDs: deleteModalState.filePatternIDs, ActiveCustomerID: activeCustomer });
        setDeleteModalState({ open: false, filePatternIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = filePatternIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, filePatternIDs: filePatternIDs });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["file_patterns"] || "File patterns"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(filePattern => filePattern.FilePatternID))
        }
    ];

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`FilePattern`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer })
        return newColumnState
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'file_pattern_delete') ? (
                <FilePatternDelete
                    activeCustomer={activeCustomer}
                    entityName={translations['file_patterns'] || 'File patterns'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleting={deleting}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        deleting: state.setting.filePatterns.deleting,
        fetching: state.setting.filePatterns.fetching
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchFilePatternsCount: filter => dispatch(actions.fetchFilePatternCount(filter)),
        onFetchFilePatterns: filter => dispatch(actions.fetchFilePattern(filter)),
        onBulkDeleteFilePatterns: filePattern => dispatch(actions.bulkDeleteFilePatterns(filePattern)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(FilePatternList));